import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useState } from "react";

type ServiceTypeFilter = "all" | "permanent" | "mobile";

interface OrganizationOption {
  id: string;
  name: string;
}

interface ServiceListFiltersProps {
  defaultOrganizationId?: string;
  selectedOrganization: string;
  onSelectedOrganizationChange: (_value: string) => void;
  organizations: OrganizationOption[];
  searchTerm: string;
  onSearchChange: (_value: string) => void;
  serviceTypeFilter: ServiceTypeFilter;
  onServiceTypeFilterChange: (_value: ServiceTypeFilter) => void;
}

/**
 * ServiceListFilters - Filters for the calculation overview services list.
 * Includes name search, service type filter, and organization filter.
 *
 * Note: Filters apply to services only (the tender card is not filtered).
 *
 * @component
 * @example
 * <ServiceListFilters
 *   selectedOrganization={selectedOrganization}
 *   onSelectedOrganizationChange={setSelectedOrganization}
 *   organizations={biddingOrganizations}
 *   searchTerm={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   serviceTypeFilter={serviceTypeFilter}
 *   onServiceTypeFilterChange={setServiceTypeFilter}
 * />
 */
function ServiceListFilters({
  defaultOrganizationId,
  selectedOrganization,
  onSelectedOrganizationChange,
  organizations,
  searchTerm,
  onSearchChange,
  serviceTypeFilter,
  onServiceTypeFilterChange,
}: ServiceListFiltersProps) {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const hasActiveFilters =
    searchTerm.trim().length > 0 ||
    serviceTypeFilter !== "all" ||
    selectedOrganization !== (defaultOrganizationId ?? "all");

  const hasPopoverFilters = serviceTypeFilter !== "all" || selectedOrganization !== (defaultOrganizationId ?? "all");

  const handleResetFilters = () => {
    onSearchChange("");
    onServiceTypeFilterChange("all");
    onSelectedOrganizationChange(defaultOrganizationId ?? "all");
  };

  const getOrganizationName = (orgId: string) => {
    const organization = organizations.find((org) => org.id === orgId);
    return organization?.name ?? orgId;
  };

  return (
    <div className="space-y-2">
      {/* Filter Controls Row */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="text"
          placeholder={t("edittender.calculationoverview.filters.searchplaceholder")}
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          className="w-80"
        />

        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={cn(hasActiveFilters && "border-primary")}
            >
              <Filter className="h-4 w-4" />
              <span className="sr-only">
                {t("edittender.calculationoverview.filters.openfilters")}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-sm">
                  {t("edittender.calculationoverview.filters.filters")}
                </h4>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("edittender.calculationoverview.filters.servicetype")}
                  </label>
                  <Select
                    value={serviceTypeFilter}
                    onValueChange={(value) => onServiceTypeFilterChange(value as ServiceTypeFilter)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("edittender.calculationoverview.filters.all")}
                      </SelectItem>
                      <SelectItem value="permanent">
                        {t("common:servicetype.permanent")}
                      </SelectItem>
                      <SelectItem value="mobile">
                        {t("common:servicetype.mobile")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("edittender.calculationoverview.common.selectorganization")}
                  </label>
                  <Select value={selectedOrganization} onValueChange={onSelectedOrganizationChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((organization) => (
                        <SelectItem key={organization.id} value={organization.id}>
                          {organization.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setIsPopoverOpen(false)}>
                  {t("common:buttons.close", { defaultValue: "Close" })}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button variant="secondary" size="icon" onClick={handleResetFilters} disabled={!hasActiveFilters}>
          <X className="h-4 w-4" />
          <span className="sr-only">
            {t("edittender.calculationoverview.filters.clearfilters")}
          </span>
        </Button>
      </div>

      {/* Active Filters Row */}
      {hasPopoverFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {serviceTypeFilter !== "all" && (
            <Badge variant="secondary" className="gap-1.5 pr-1 py-1">
              <span className="text-xs text-muted-foreground">
                {t("edittender.calculationoverview.filters.servicetype")}:
              </span>
              <span className="text-xs font-semibold">
                {t(`common:servicetype.${serviceTypeFilter}`)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => onServiceTypeFilterChange("all")}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">
                  {t("edittender.calculationoverview.filters.removefilter")}
                </span>
              </Button>
            </Badge>
          )}

          {selectedOrganization !== (defaultOrganizationId ?? "all") && (
            <Badge variant="secondary" className="gap-1.5 pr-1 py-1">
              <span className="text-xs text-muted-foreground">
                {t("edittender.calculationoverview.common.selectorganization")}:
              </span>
              <span className="text-xs font-semibold">
                {getOrganizationName(selectedOrganization)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => onSelectedOrganizationChange(defaultOrganizationId ?? "all")}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">
                  {t("edittender.calculationoverview.filters.removefilter")}
                </span>
              </Button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export default ServiceListFilters;
